import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { track } from "@/lib/track";

const STEP = 300;
const MOVE = 700;

export const AD_FONTS = [
  { key: "bold", label: "Të trasha", css: "'Plus Jakarta Sans', sans-serif", weight: 800 },
  { key: "hand", label: "Me dorë", css: "'Caveat', cursive", weight: 700 },
  { key: "script", label: "Kaligrafi", css: "'Pacifico', cursive", weight: 400 },
  { key: "elegant", label: "Elegante", css: "'Playfair Display', serif", weight: 700 },
  { key: "poster", label: "Poster", css: "'Bebas Neue', sans-serif", weight: 400 },
  { key: "fun", label: "Argëtuese", css: "'Lobster', cursive", weight: 400 },
  { key: "modern", label: "Moderne", css: "'Montserrat', sans-serif", weight: 900 },
];
export const fontStyle = (k) => { const f = AD_FONTS.find((x) => x.key === k) || AD_FONTS[0]; return { fontFamily: f.css, fontWeight: f.weight }; };

export const AD_ANIMS = [
  { key: "side", label: "Anash" },
  { key: "left", label: "Nga e majta" },
  { key: "right", label: "Nga e djathta" },
  { key: "top", label: "Nga lart" },
  { key: "bottom", label: "Nga poshtë" },
  { key: "zoom", label: "Zmadhim" },
  { key: "type", label: "Shkronjë pas shkronje" },
  { key: "fade", label: "Shfaqje e butë" },
];

const offTransform = (anim, fromLeft) => ({
  top: "translateY(-420px)", bottom: "translateY(420px)", zoom: "scale(0.15)", fade: "none", type: "none", left: "translateX(-130vw)", right: "translateX(130vw)",
}[anim] || `translateX(${fromLeft ? "-130vw" : "130vw"})`);

function useTyped(text, active, delay) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) { setN(0); return undefined; }
    let iv;
    const t = setTimeout(() => { iv = setInterval(() => setN((k) => { if (k >= text.length) { clearInterval(iv); return k; } return k + 1; }), 55); }, delay);
    return () => { clearTimeout(t); clearInterval(iv); };
  }, [text, active, delay]);
  return n;
}

export const lineVisible = (l) => !!(l.image || (l.text || "").trim());

function FitText({ text, size, typed }) {
  const ref = useRef(null);
  const [fs, setFs] = useState(size);
  useLayoutEffect(() => {
    const el = ref.current;
    const box = el?.closest("[data-fit-box]");
    if (!el || !box) return undefined;
    const fit = () => {
      el.style.fontSize = `${size}px`;
      const max = (box.clientWidth - 40) * 0.96;
      const w = el.offsetWidth;
      const next = w > max && max > 0 ? Math.max(10, Math.floor((size * max) / w)) : size;
      el.style.fontSize = `${next}px`;
      setFs(next);
    };
    fit();
    document.fonts?.ready.then(fit);
    const ro = new ResizeObserver(fit);
    ro.observe(box);
    return () => ro.disconnect();
  }, [text, size]);
  const shown = typed == null ? text : <>{text.slice(0, typed)}<span style={{ opacity: 0 }}>{text.slice(typed)}</span></>;
  return <span ref={ref} className="whitespace-nowrap" style={{ fontSize: `${fs}px` }}>{shown}</span>;
}

function LineContent({ line, typed }) {
  if (!line.image) return <FitText text={line.text} size={line.size || 24} typed={typed} />;
  return <img src={line.image} alt="" draggable={false} className="inline-block max-w-full object-contain pointer-events-none" style={{ height: `${(line.size || 24) * 3}px`, filter: "drop-shadow(0 2px 8px rgba(0,0,0,.45))" }} />;
}

function AdLine({ line, i, phase }) {
  const fromLeft = i % 2 === 0;
  const off = phase !== "show";
  const placed = line.x != null && line.y != null;
  const anim = line.image && line.anim === "type" ? "fade" : (line.anim || "side");
  const isType = anim === "type";
  const typed = useTyped(line.text || "", isType && phase === "show", i * STEP);
  const slide = off ? offTransform(anim, fromLeft) : (anim === "zoom" ? "scale(1)" : "translateX(0)");
  return (
    <div data-testid={`home-ad-line-${i}`}
      className={`font-extrabold leading-tight whitespace-nowrap ${placed ? "absolute" : ""}`}
      style={{
        color: line.color, fontSize: `${line.size || 24}px`, textShadow: "0 2px 8px rgba(0,0,0,.55)", ...fontStyle(line.font),
        ...(placed ? { left: `${line.x}%`, top: `${line.y}%`, transform: `translate(-50%, -50%) ${slide}`, textAlign: "center" } : { transform: slide, textAlign: fromLeft ? "left" : "right" }),
        opacity: off && !(isType && phase === "enter") ? 0 : 1,
        transition: `transform ${MOVE}ms cubic-bezier(.2,.8,.2,1) ${i * STEP}ms, opacity ${MOVE}ms ease ${i * STEP}ms`,
      }}><LineContent line={line} typed={isType ? typed : null} /></div>
  );
}

function DragLine({ line, i, boxRef, onMove }) {
  const placed = line.x != null && line.y != null;
  const start = (e) => {
    e.preventDefault();
    const el = e.currentTarget;
    el.setPointerCapture?.(e.pointerId);
    const move = (ev) => {
      const r = boxRef.current.getBoundingClientRect();
      onMove(i, Math.round(Math.max(0, Math.min(100, ((ev.clientX - r.left) / r.width) * 100)) * 10) / 10, Math.round(Math.max(0, Math.min(100, ((ev.clientY - r.top) / r.height) * 100)) * 10) / 10);
    };
    const up = () => { el.removeEventListener("pointermove", move); el.removeEventListener("pointerup", up); el.removeEventListener("pointercancel", up); };
    el.addEventListener("pointermove", move); el.addEventListener("pointerup", up); el.addEventListener("pointercancel", up);
  };
  return (
    <div data-testid={`homead-drag-line-${i}`} onPointerDown={start}
      className={`font-extrabold leading-tight whitespace-nowrap cursor-move select-none outline-dashed outline-2 outline-white/70 rounded px-1 ${placed ? "absolute" : ""}`}
      style={{
        color: line.color, fontSize: `${line.size || 24}px`, textShadow: "0 2px 8px rgba(0,0,0,.55)", touchAction: "none", ...fontStyle(line.font),
        ...(placed ? { left: `${line.x}%`, top: `${line.y}%`, transform: "translate(-50%, -50%)", textAlign: "center" } : { textAlign: i % 2 === 0 ? "left" : "right" }),
      }}><LineContent line={line} /></div>
  );
}

export function HomeAdEditBox({ slide, onMoveLine }) {
  const ref = React.useRef(null);
  return (
    <div ref={ref} data-testid="homead-edit-box" className="relative w-full aspect-square rounded-3xl overflow-hidden shadow-lg bg-slate-200">
      <img src={slide.image} alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
      <div data-fit-box className="absolute inset-0 flex flex-col justify-center gap-2 px-5">
        {slide.lines.map((l, i) => <DragLine key={i} line={l} i={i} boxRef={ref} onMove={onMoveLine} />)}
      </div>
    </div>
  );
}

export function HomeAdView({ slides, tid = "home-ad", inAdmin = false }) {
  const [idx, setIdx] = useState(0);
  const [cycle, setCycle] = useState(0);
  const [phase, setPhase] = useState("enter");
  const sig = JSON.stringify(slides);

  useEffect(() => { setIdx(0); setPhase("enter"); setCycle((c) => c + 1); }, [sig]);
  useEffect(() => {
    if (inAdmin || !slides.length) return;
    const sl = slides[idx % slides.length];
    track("ad_view", { ad: (sl.link || sl.image || "").slice(-80), cat: "home" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, sig]);

  useEffect(() => {
    if (!slides.length) return undefined;
    const s = slides[idx % slides.length];
    const n = s?.lines.length || 0;
    const stay = (s?.stay || 10) * 1000;
    const anim = n * STEP + MOVE;
    const t1 = setTimeout(() => setPhase("show"), 80);
    const t2 = setTimeout(() => setPhase("leave"), 80 + anim + stay);
    const t3 = setTimeout(() => {
      setPhase("enter");
      setIdx((k) => (k + 1) % slides.length);
      setCycle((c) => c + 1);
    }, 80 + 2 * anim + stay + 300);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [cycle]);

  if (!slides.length) return null;
  const s = slides[idx % slides.length];
  const clickable = !inAdmin && s.link_on && s.link;
  const Wrap = clickable ? "a" : "div";
  const adId = (s.link || s.image || "").slice(-80);
  const wrapProps = clickable ? { href: s.link, target: s.link.startsWith("/") ? undefined : "_blank", rel: "noopener noreferrer", onClick: () => track("ad_click", { ad: adId, cat: "home" }) } : {};

  return (
    <Wrap {...wrapProps} data-testid={tid} className="block relative w-full aspect-square rounded-3xl overflow-hidden shadow-lg bg-slate-200">
      {slides.map((sl, i) => (
        <img key={sl.image + i} src={sl.image} alt="" data-testid={i === idx % slides.length ? `${tid}-image` : undefined}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700" style={{ opacity: i === idx % slides.length ? 1 : 0 }} />
      ))}
      <div data-fit-box className="absolute inset-0 flex flex-col justify-center gap-2 px-5 overflow-hidden">
        {s.lines.map((l, i) => <AdLine key={`${cycle}-${i}`} line={l} i={i} phase={phase} />)}
      </div>
      {slides.length > 1 && (
        <div className="absolute bottom-3 inset-x-0 flex justify-center gap-1.5">
          {slides.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx % slides.length ? "w-5 bg-white" : "w-1.5 bg-white/60"}`} />)}
        </div>
      )}
    </Wrap>
  );
}

export function HomeAd() {
  const [slides, setSlides] = useState([]);
  useEffect(() => { api.get("/home-ad").then((r) => setSlides(r.data.slides || [])).catch(() => {}); }, []);
  if (!slides.length) return null;
  return <section className="px-4 mt-6"><HomeAdView slides={slides} /></section>;
}

export default HomeAd;
